import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { startCheckout } from "@/lib/checkout";
import { TZ, fmtFull } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, BarChart3, CalendarDays, CheckCircle2, HeartHandshake, Info, MonitorPlay, UserRound } from "lucide-react";

const THEMES = {
  analytics: {
    page: "bg-[#09090B]", hero: "grid-bg-adult", glow: null,
    accent: "text-amber-400", pill: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    btn: "bg-amber-500 hover:bg-amber-400 text-black glow-amber", card: "border-zinc-800 bg-zinc-950", soft: "border-zinc-800",
    muted: "text-zinc-400", Icon: BarChart3, label: "Live workshop · Data",
  },
  healing: {
    page: "bg-[#120C0C]", hero: "",
    glow: "radial-gradient(ellipse at 85% 0%, rgba(251,113,133,0.22), transparent 55%), radial-gradient(ellipse at 0% 100%, rgba(253,186,116,0.12), transparent 50%)",
    accent: "text-rose-300", pill: "bg-rose-400/10 border-rose-300/30 text-rose-200",
    btn: "bg-rose-300 hover:bg-rose-200 text-[#2A1414] shadow-[0_12px_40px_-8px_rgba(253,164,175,0.45)]", card: "border-[#3A2424] bg-[#1A1111]", soft: "border-[#3A2424]",
    muted: "text-[#CDB7B1]", Icon: HeartHandshake, label: "Live workshop · Healing",
  },
};

export default function Workshop() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [missing, setMissing] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const { currency, format } = useCurrency();
  const nav = useNavigate();

  useEffect(() => { api.get(`/catalog/workshops/${slug}`).then((r) => setW(r.data)).catch(() => setMissing(true)); }, [slug]);
  useEffect(() => {
    if (!user) { return; }
    api.get("/dashboard/me").then((r) => setEnrolled((r.data.workshops || []).some((x) => x.workshop_id === slug))).catch(() => {});
  }, [user, slug]);

  const enrol = async () => {
    if (!user) { toast.info("Please sign in to enrol"); nav(`/login?next=/workshops/${slug}`); return; }
    setBusy(true);
    try { await startCheckout({ item_kind: "workshop", item_id: slug, currency, timezone: TZ }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed"); }
    finally { setBusy(false); }
  };

  if (missing) return <WorkshopMissing />;
  if (!w) return <div className="min-h-screen bg-[#09090B] text-zinc-400 flex items-center justify-center">Loading…</div>;
  const t = THEMES[w.theme] || THEMES.analytics;
  const price = format(w[`price_${currency}`]);
  const when = w.start_time_iso ? fmtFull(w.start_time_iso) : "Date announced soon";
  const cta = { t, price, enrolled, busy, onEnrol: enrol };

  return (
    <div className={`min-h-screen text-white ${t.page}`} data-testid={`workshop-page-${slug}`}>
      <Navbar variant="adult" />
      <section className="relative overflow-hidden">
        <div className={`absolute inset-0 ${t.hero}`} style={t.glow ? { backgroundImage: t.glow } : undefined} />
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-10 pb-20 relative">
          <Link to="/meet-sankalp" data-testid="workshop-back" className={`inline-flex items-center gap-2 text-sm ${t.muted} hover:text-white transition-colors`}>
            <ArrowLeft className="w-4 h-4" /> Meet Sankalp
          </Link>
          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs uppercase tracking-[0.2em] font-adult-mono ${t.pill}`}>
                <t.Icon className="w-3.5 h-3.5" />{t.label}
              </div>
              <h1 data-testid="workshop-title" className="mt-6 font-adult-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]">{w.title}</h1>
              <p className={`mt-6 text-lg leading-relaxed max-w-2xl ${t.muted}`}>{w.tagline}</p>
              <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl">
                <Fact t={t} Icon={CalendarDays} label="When" value={when} testid="workshop-when" />
                <Fact t={t} Icon={MonitorPlay} label="Format" value={w.duration_min ? `Live online · ${w.duration_min} min` : "Live online"} />
                <Fact t={t} Icon={UserRound} label="Host" value="Sankalp Sidhanth" />
              </div>
            </div>
            <EnrolCard {...cta} when={when} />
          </div>
        </div>
      </section>
      <WorkshopDetails w={w} t={t} />
      <BottomCta {...cta} when={when} />
      <Footer variant="adult" />
    </div>
  );
}

function Fact({ t, Icon, label, value, testid }) {
  return (
    <div className={`rounded-xl border p-4 bg-black/30 ${t.soft}`}>
      <Icon className={`w-4 h-4 mb-2 ${t.accent}`} />
      <div className="text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-500">{label}</div>
      <div data-testid={testid} className="mt-1 text-sm text-zinc-100 leading-snug">{value}</div>
    </div>
  );
}

function EnrolButton({ t, price, enrolled, busy, onEnrol, testid, label }) {
  if (enrolled) {
    return <div data-testid={`${testid}-enrolled`} className={`rounded-full border text-center py-3 px-6 text-sm font-semibold ${t.pill}`}>✓ You&apos;re enrolled</div>;
  }
  return (
    <Button data-testid={testid} onClick={onEnrol} disabled={busy} className={`h-12 px-8 rounded-full font-semibold text-base ${t.btn}`}>
      {busy ? "Opening checkout…" : label || `Enrol for ${price}`}
    </Button>
  );
}

function EnrolCard({ when, ...cta }) {
  const { t, price } = cta;
  return (
    <div className={`lg:col-span-5 rounded-3xl border p-6 md:p-8 ${t.card}`} data-testid="workshop-enrol-card">
      <div className={`text-xs uppercase tracking-[0.2em] font-adult-mono ${t.muted}`}>Your seat</div>
      <div data-testid="workshop-price" className={`mt-2 font-adult-mono text-4xl font-semibold ${t.accent}`}>{price}</div>
      <div className={`mt-1 text-sm ${t.muted}`}>One live session · {when}</div>
      <ul className="mt-6 space-y-2.5 text-sm text-zinc-200">
        {["Live with Sankalp, including Q&A", "Joining link emailed before it starts", "Instant confirmation by email"].map((x) => (
          <li key={x} className="flex items-start gap-2.5"><CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${t.accent}`} />{x}</li>
        ))}
      </ul>
      <div className="mt-6 grid"><EnrolButton {...cta} testid="workshop-enrol-btn" /></div>
    </div>
  );
}

function WorkshopDetails({ w, t }) {
  return (
    <section className="max-w-7xl mx-auto px-6 pb-16 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-7">
        <div className={`text-[11px] uppercase tracking-[0.28em] font-adult-mono ${t.accent}`}>About this workshop</div>
        <p className="mt-4 text-zinc-200 text-lg leading-relaxed">{w.description}</p>
        <div className={`mt-12 text-[11px] uppercase tracking-[0.28em] font-adult-mono ${t.accent}`}>What you&apos;ll take away</div>
        <ol className="mt-5 space-y-3" data-testid="workshop-highlights">
          {w.highlights.map((h, i) => (
            <li key={h} className={`flex items-start gap-4 rounded-2xl border p-4 ${t.soft}`}>
              <span className={`font-adult-mono text-sm ${t.accent}`}>0{i + 1}</span>
              <span className="text-zinc-100">{h}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className="lg:col-span-5 space-y-6">
        <div className={`rounded-3xl border p-6 ${t.card}`}>
          <div className={`text-[11px] uppercase tracking-[0.28em] font-adult-mono ${t.accent}`}>Who it&apos;s for</div>
          <ul className="mt-4 space-y-3">
            {w.for_whom.map((x) => (
              <li key={x} className="flex gap-3 text-sm text-zinc-200 leading-relaxed"><CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${t.accent}`} />{x}</li>
            ))}
          </ul>
        </div>
        <HostCard t={t} />
        {w.disclaimer && (
          <div data-testid="workshop-disclaimer" className={`rounded-2xl border p-5 text-xs leading-relaxed flex gap-3 ${t.soft} ${t.muted}`}>
            <Info className="w-4 h-4 shrink-0" />{w.disclaimer}
          </div>
        )}
      </div>
    </section>
  );
}

function HostCard({ t }) {
  const [photo, setPhoto] = useState("");
  useEffect(() => { api.get("/catalog/founder").then((r) => setPhoto(r.data.photo_url)).catch(() => {}); }, []);
  return (
    <Link to="/meet-sankalp" data-testid="workshop-host-card" className={`flex items-center gap-4 rounded-3xl border p-5 hover:-translate-y-0.5 transition-smooth ${t.card}`}>
      {photo ? <img src={photo} alt="Sankalp Sidhanth" className="w-16 h-16 rounded-2xl object-cover object-top" /> : <div className="w-16 h-16 rounded-2xl bg-zinc-800" />}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-500">Your host</div>
        <div className="font-adult-heading text-lg">Sankalp Sidhanth</div>
        <div className={`text-xs ${t.accent}`}>Meet Sankalp →</div>
      </div>
    </Link>
  );
}

function BottomCta({ when, ...cta }) {
  const { t, price } = cta;
  return (
    <section className="max-w-7xl mx-auto px-6 pb-24">
      <div className={`rounded-3xl border p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6 ${t.card}`}>
        <div>
          <div className="font-adult-heading text-2xl md:text-3xl font-semibold tracking-tight">
            Save your seat for <span className={`font-adult-serif italic ${t.accent}`}>{price}</span>
          </div>
          <div className={`mt-2 text-sm ${t.muted}`}>{when} · Live online</div>
        </div>
        <EnrolButton {...cta} testid="workshop-enrol-btn-bottom" label="Enrol now" />
      </div>
    </section>
  );
}

function WorkshopMissing() {
  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar variant="adult" />
      <div data-testid="workshop-missing" className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-adult-heading text-3xl font-semibold">This workshop isn&apos;t available.</h1>
        <Link to="/meet-sankalp" className="mt-6 inline-block text-amber-400 hover:text-amber-300">See all workshops →</Link>
      </div>
    </div>
  );
}
